import React, {
	useCallback, useEffect, useMemo, useState,
} from 'react';
import { capitalize, toLower } from 'lodash';

import { CampusAssignedProgramVersion, ProgramVersionEntity, StudyPlanEntity } from 'Models/Entities';

import Modal from 'Views/Components/Modal/Modal';
import { Select, SelectOption } from 'Views/Components/Select/Select';
import { Button, Display } from 'Views/Components/Button/Button';

import { useMounted, useOnMount } from 'Hooks/useMounted';
import { getProgramVersionNamesWithCampus } from 'Queries/GraphQlQueries/ProgramVersion/ProgramVersionQueries';
import { getStudyPlansByName } from 'Queries/GraphQlQueries/StudyPlan/StudyPlanQueries';
import { observer, useLocalStore } from 'mobx-react';
import { action, runInAction } from 'mobx';
import { TextField } from 'Views/Components/TextBox/TextBox';
import { DisplayType } from 'Views/Components/Models/Enums';
import ProgramSearch from 'Views/Components/Program/ProgramSearch';
import StatusChip from 'Views/Components/Chip/ProgramVersionStatusChip';
import { programStatus, programStatusOptions } from 'Models/Enums';
import { Checkbox } from 'Views/Components/Checkbox/Checkbox';
import { CheckboxGroup } from 'Views/Components/Checkbox/CheckboxGroup';
import useStore from 'Hooks/useStore';
import { Conditional } from 'Views/Components/Conditional/Conditional';

interface ICreateStudyPlanModalProps {
	isOpen: boolean;
	closeModal: () => any;
	confirmModal: (studyPlan: StudyPlanEntity) => any,
	initialPlan: StudyPlanEntity,
	initialProgramId?: string,
}

enum FieldMap {
	PROGRAM = 0,
	PROGRAM_VERSION = 1,
	CAMPUS = 2,
	PLAN_NAME = 9999,
}

export const createStudyPlanName = async (
	programName: string,
	commencingYear: string,
) => {
	const newName = `${programName} - ${commencingYear}`;

	const { studyPlanEntitys: existingPlansWithSameName } = await getStudyPlansByName(newName);

	const optionalSuffix = existingPlansWithSameName.length > 0
		? ` ${existingPlansWithSameName.length}`
		: '';

	return newName + optionalSuffix;
};

const getCampusOptions = (
	versionCampusJoins: CampusAssignedProgramVersion[],
): SelectOption<string>[] => {
	const unsortedOptions = versionCampusJoins
		.map(campuss => ({
			display: campuss.campus.name,
			value: campuss.campus.id,
		}));

	const sortOptions = unsortedOptions
		.sort((a, b) => a.display.localeCompare(b.display));

	return sortOptions;
};

const CreateStudyPlanModal = observer((
	{
		isOpen, closeModal, confirmModal, initialPlan, initialProgramId,
	}: ICreateStudyPlanModalProps,
) => {
	const mounted = useMounted();
	const { userGroups } = useStore();
	const [programVersions, setProgramVersions] = useState<ProgramVersionEntity[]>();

	const programDropdownState = useLocalStore(() => ({ programId: initialProgramId }));
	const commencingYearFilterState = useLocalStore<{
		[key in programStatus]: boolean;
	}>(() => ({
		PUBLISHED: true,
		IN_REVIEW: true,
		DRAFT: true,
		ARCHIVED: true,
		APPROVED: true,
	}));
	const studyPlan = useLocalStore(() => new StudyPlanEntity(initialPlan));

	const [isLoadingPlanName, setIsLoadingPlanName] = useState(false);

	const [errors, setErrors] = useState<Array<string | undefined>>([]);

	useOnMount(async (isMounted, abortSignals) => {
		if (!programDropdownState.programId) {
			return;
		}

		const responseData = await getProgramVersionNamesWithCampus(
			abortSignals,
			[[
				{
					path: 'program.id',
					comparison: 'equal',
					value: [programDropdownState.programId],
				},
			]],
		);

		if (!isMounted()) {
			return;
		}

		const retrievedProgramVersions = responseData.data.programVersionEntitys
			.map(programAttributes => new ProgramVersionEntity(programAttributes));

		setProgramVersions(retrievedProgramVersions);
	}, [programDropdownState.programId]);

	const isNameInputDisabled = useMemo(() => (
		!studyPlan.programVersionId || !studyPlan.campusId || isLoadingPlanName
	), [isLoadingPlanName, studyPlan.campusId, studyPlan.programVersionId]);

	const selectedProgramVersion = programVersions
		? programVersions
			.find(version => studyPlan.programVersionId === version.id)
		: undefined;

	const selectedProgram = selectedProgramVersion?.program;

	const campusDropdownList = getCampusOptions(selectedProgramVersion?.campuss || []);

	const setPlanName = useCallback(async (
		programName: string | undefined,
		commencingYear: string | undefined,
	) => {
		if (!programName || !commencingYear) {
			return;
		}

		const newName = await createStudyPlanName(programName, commencingYear);

		if (mounted.current) {
			runInAction(() => {
				studyPlan.name = newName;
			});
		}
	}, [mounted, studyPlan]);

	useEffect(() => {
		if (
			studyPlan.programVersionId
			&& studyPlan.campusId
			&& selectedProgram
		) {
			setIsLoadingPlanName(true);
			setPlanName(
				selectedProgram.displayName,
				selectedProgramVersion.commencingYear.toString(),
			).then(() => {
				if (mounted.current) {
					setIsLoadingPlanName(false);
				}
			});
		}
	}, [
		mounted,
		selectedProgram,
		selectedProgramVersion,
		setPlanName,
		studyPlan.campusId,
		studyPlan.programVersionId,
	]);

	const clearFieldError = (field: FieldMap) => {
		setErrors(currentErrors => {
			const newErrors = [...currentErrors];
			newErrors[field] = undefined;
			return newErrors;
		});
	};

	const clearField = action(
		(field: FieldMap) => {
			switch (field) {
				case FieldMap.PROGRAM:
					programDropdownState.programId = undefined;

					setErrors(currentErrors => {
						const newErrors = [...currentErrors];
						newErrors[FieldMap.PROGRAM] = undefined;
						return newErrors;
					});
					break;
				case FieldMap.PROGRAM_VERSION:
					// @ts-expect-error
					studyPlan.programVersionId = undefined;

					setErrors(currentErrors => {
						const newErrors = [...currentErrors];
						newErrors[FieldMap.PROGRAM_VERSION] = undefined;
						return newErrors;
					});
					break;
				case FieldMap.CAMPUS:
					// @ts-expect-error
					studyPlan.campusId = undefined;

					setErrors(currentErrors => {
						const newErrors = [...currentErrors];
						newErrors[FieldMap.CAMPUS] = undefined;
						return newErrors;
					});
					break;
				case FieldMap.PLAN_NAME:
					// @ts-expect-error
					studyPlan.name = undefined;
					break;
				default:
					throw new Error('Reached unreachable point');
			}
		},
	);

	// From form bottom to top, clear fields and their errors,
	// optionally based on some point of reference within form
	const clearFromField = (referenceField?: FieldMap.PROGRAM | FieldMap.PROGRAM_VERSION) => {
		runInAction(() => {
			clearField(FieldMap.CAMPUS);

			// if clearing from program version onwards
			// return early to not cear any prior fields
			if (referenceField === FieldMap.PROGRAM_VERSION) {
				return;
			}

			clearField(FieldMap.PROGRAM_VERSION);

			// if clearing from program onwards
			// return early to not cear any prior fields
			if (referenceField === FieldMap.PROGRAM) {
				return;
			}

			clearField(FieldMap.PROGRAM);
			clearField(FieldMap.PLAN_NAME);
		});
	};

	const onCancel = () => {
		clearFromField();
		runInAction(() => {
			commencingYearFilterState.PUBLISHED = true;
			commencingYearFilterState.APPROVED = true;
			commencingYearFilterState.IN_REVIEW = true;
			commencingYearFilterState.DRAFT = true;
			commencingYearFilterState.ARCHIVED = true;
		});
		closeModal();
	};

	const onConfirm = async () => {
		if (!programDropdownState.programId) {
			setErrors(prevState => {
				const newState = [...prevState];
				newState[0] = `A ${toLower(window.lexicon.program)} must be selected`;
				return newState;
			});
		}
		if (!studyPlan.programVersionId) {
			setErrors(prevState => {
				const newState = [...prevState];
				newState[1] = `A ${toLower(window.lexicon.commencingYear)} must be specified`;
				return newState;
			});
		}
		if (!studyPlan.campusId) {
			setErrors(prevState => {
				const newState = [...prevState];
				newState[2] = `A ${toLower(window.lexicon.location)} must be specified`;
				return newState;
			});
		}
		if (!studyPlan.name) {
			setErrors(prevState => {
				const newState = [...prevState];
				newState[3] = 'A name must be specified';
				return newState;
			});
		}
		if (
			studyPlan.programVersionId
			&& studyPlan.campusId
			&& selectedProgram
			&& studyPlan.name
		) {
			confirmModal(new StudyPlanEntity(studyPlan));
		}
	};

	const userCanAccessVersionStatus = useCallback((status: programStatus) => {
		if (userGroups.some(ug => ug.name === 'Admin')) {
			return true;
		}

		switch (status) {
			case 'PUBLISHED':
				return true;
			case 'APPROVED':
				return !userGroups.some(ug => ug.name === 'User');
			case 'IN_REVIEW':
				return !userGroups.some(ug => ug.name === 'User');
			case 'DRAFT':
				return false;
			case 'ARCHIVED':
				return !userGroups.some(ug => ug.name === 'User');
			default:
				return false;
		}
	}, [userGroups]);

	const filterProgramVersionsByStatus = useCallback((
		programVersionEntities: ProgramVersionEntity[],
		status: programStatus,
	) => {
		if (
			!userCanAccessVersionStatus(status)
			|| !commencingYearFilterState[status]
			|| !programVersionEntities
		) {
			return [];
		}

		return programVersionEntities
			.filter(version => version.status === status)
			.sort((a, b) => b.versionDisplayName.localeCompare(a.versionDisplayName));
	}, [commencingYearFilterState, userCanAccessVersionStatus]);

	const getCommencingYearOptions = useCallback((): SelectOption<string>[] => {
		if (!programVersions) {
			return [];
		}

		const publishedProgramVersions = filterProgramVersionsByStatus(
			programVersions,
			'PUBLISHED',
		);
		const approvedProgramVersions = filterProgramVersionsByStatus(
			programVersions,
			'APPROVED',
		);
		const inReviewProgramVersions = filterProgramVersionsByStatus(
			programVersions,
			'IN_REVIEW',
		);
		const draftProgramVersions = filterProgramVersionsByStatus(
			programVersions,
			'DRAFT',
		);
		const archivedProgramVersions = filterProgramVersionsByStatus(
			programVersions,
			'ARCHIVED',
		);

		return [
			...publishedProgramVersions,
			...approvedProgramVersions,
			...inReviewProgramVersions,
			...draftProgramVersions,
			...archivedProgramVersions,
		].map(version => ({
			display: (
				<div className="program-version-option">
					{version.getDisplayName()}
					<Conditional
						condition={!userGroups.some(ug => ug.name === 'User')}
						component={() => (
							<StatusChip status={programStatusOptions[version.status]} />
						)}
						componentProps={{}}
					/>
				</div>
			),
			value: version.id,
		}));
	}, [filterProgramVersionsByStatus, programVersions, userGroups]);

	return (
		<Modal
			className="create-study-plan-modal"
			isOpen={isOpen}
			label={`New ${toLower(window.lexicon.studyPlan)}`}
			onRequestClose={onCancel}
		>
			<h3>New {toLower(window.lexicon.studyPlan)}</h3>
			<ProgramSearch<typeof programDropdownState>
				label={`${capitalize(window.lexicon.program)} title`}
				placeholder={`Search for ${toLower(window.lexicon.program)}`}
				model={programDropdownState}
				modelProperty="programId"
				searchByProperty="displayName"
				optionValueProperty="id"
				onAfterChange={newValue => {
					if (typeof newValue === 'string') {
						clearFromField(FieldMap.PROGRAM);
					}
				}}
				isRequired
				searchable
				errors={errors[0]}
			/>
			<div className="commencing-year-wrapper">
				<p className="commencing-year-label">
					{capitalize(window.lexicon.commencingYear)}<span className="required">*</span>
				</p>
				<Conditional
					condition={!userGroups.some(ug => ug.name === 'User')}
					component={() => (
						<CheckboxGroup
							id="commencing-year-checkbox-group"
						>
							<Checkbox
								id="published-checkbox"
								label="Published"
								model={commencingYearFilterState}
								modelProperty="PUBLISHED"
							/>
							<Conditional
								condition={
									userCanAccessVersionStatus('APPROVED')
								}
								component={() => (
									<Checkbox
										id="approved-checkbox"
										label="Approved"
										model={commencingYearFilterState}
										modelProperty="APPROVED"
									/>
								)}
								componentProps={{}}
							/>
							<Conditional
								condition={
									userCanAccessVersionStatus('IN_REVIEW')
								}
								component={() => (
									<Checkbox
										id="in-review-checkbox"
										label="In review"
										model={commencingYearFilterState}
										modelProperty="IN_REVIEW"
									/>
								)}
								componentProps={{}}
							/>
							<Conditional
								condition={userCanAccessVersionStatus('DRAFT')}
								component={() => (
									<Checkbox
										id="draft-checkbox"
										label="Draft"
										model={commencingYearFilterState}
										modelProperty="DRAFT"
									/>
								)}
								componentProps={{}}
							/>
							<Conditional
								condition={userCanAccessVersionStatus('ARCHIVED')}
								component={() => (
									<Checkbox
										id="archived-checkbox"
										label="Archived"
										model={commencingYearFilterState}
										modelProperty="ARCHIVED"
									/>
								)}
								componentProps={{}}
							/>
						</CheckboxGroup>
					)}
					componentProps={{}}
				/>
				<Select<StudyPlanEntity, string>
					labelVisible={false}
					label={capitalize(window.lexicon.commencingYear)}
					placeholder="Select year"
					model={studyPlan}
					modelProperty="programVersionId"
					options={getCommencingYearOptions()}
					onAfterChange={newValue => {
						if (typeof newValue === 'string') {
							clearFromField(FieldMap.PROGRAM_VERSION);
						}
					}}
					isRequired
					errors={errors[1]}
					isDisabled={!programDropdownState.programId}
				/>
			</div>
			<Select<StudyPlanEntity, string>
				label={capitalize(window.lexicon.location)}
				placeholder={capitalize(window.lexicon.location)}
				model={studyPlan}
				modelProperty="campusId"
				options={campusDropdownList}
				onAfterChange={newValue => {
					if (newValue && 'value' in newValue && typeof newValue.value === 'string') {
						clearFieldError(FieldMap.CAMPUS);
					}
				}}
				isRequired
				errors={errors[2]}
				isDisabled={!studyPlan.programVersionId}
			/>
			<TextField
				model={studyPlan}
				modelProperty="name"
				id="study-plan-name"
				name="study-plan-name"
				displayType={DisplayType.BLOCK}
				label="Name"
				labelVisible
				placeholder={`Name your ${toLower(window.lexicon.studyPlan)}`}
				onAfterChange={evt => {
					clearFieldError(FieldMap.CAMPUS);
				}}
				isRequired
				errors={errors[3]}
				isDisabled={isNameInputDisabled}
			/>
			<div key="actions" className="modal__actions">
				<Button key="cancel" className="modal--cancel" onClick={onCancel} display={Display.Text}>
					Cancel
				</Button>
				<Button
					key="confirm"
					className="modal--confirm"
					testId="next-btn"
					onClick={onConfirm}
					display={Display.Solid}
				>
					Create {toLower(window.lexicon.studyPlan)}
				</Button>
			</div>
		</Modal>
	);
});

export default CreateStudyPlanModal;
